import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Cpu, Video, Image as ImageIcon, Code2, Volume2, UserCheck } from "lucide-react";
import { Header } from "@/pages/Operations";
import api from "@/lib/api";

const catIcon = { Video, Image: ImageIcon, "Code & LLM": Code2, "Audio & Dubbing": Volume2, Avatar: UserCheck };
const statusColor = {
  Operational: "text-emerald-400 bg-emerald-500/10 border-emerald-500/25",
  Degraded: "text-amber-400 bg-amber-500/10 border-amber-500/25",
  Maintenance: "text-red-400 bg-red-500/10 border-red-500/25",
};

export default function Engines() {
  const [engines, setEngines] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/engines").then((r) => setEngines(r.data)).catch(() => {});
  }, []);

  const cats = ["All", ...Array.from(new Set(engines.map((e) => e.category)))];
  const shown = filter === "All" ? engines : engines.filter((e) => e.category === filter);

  return (
    <div data-testid="engine-manager-grid" className="p-5 sm:p-8 max-w-[1400px] mx-auto">
      <Header
        icon={Cpu}
        eyebrow="Cinematic Arsenal · Capability Catalog"
        title="Engine Manager"
        subtitle="Capability catalog with live provider status — which routes are configured and working right now."
      />

      <div className="flex flex-wrap gap-2 mb-6">
        {cats.map((c) => (
          <button
            key={c}
            data-testid={`engine-filter-${c.replace(/[^a-z]/gi, "").toLowerCase()}`}
            onClick={() => setFilter(c)}
            className={`px-3.5 py-1.5 rounded-full text-xs border transition-colors ${
              filter === c
                ? "bg-gradient-to-r from-purple-600/25 to-cyan-500/15 border-purple-500/40 text-white"
                : "border-white/10 text-slate-400 hover:text-white hover:bg-white/5"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.map((e, i) => {
          const Icon = catIcon[e.category] || Cpu;
          return (
            <motion.div
              key={e.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (i % 12) * 0.03 }}
              data-testid={`engine-card-${i}`}
              className="glass rounded-2xl p-4 hover:border-purple-500/40 transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600/30 to-cyan-500/20 flex items-center justify-center">
                  <Icon size={18} className="text-cyan-300" />
                </div>
                <span className={`text-[9px] font-mono uppercase px-2 py-1 rounded-md border ${statusColor[e.status]}`}>
                  {e.status}
                </span>
              </div>
              <div className="font-display font-semibold text-sm truncate">{e.name}</div>
              <div className="text-[10px] font-mono text-slate-500">{e.category}</div>
              <div className="mt-3 flex items-center justify-between text-[10px] font-mono text-slate-500">
                <span>⚡ {e.latency}</span>
                <span>{e.cost}</span>
                <span className="text-emerald-400">{e.uptime}%</span>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
